// Shared dynamic column-derivation for the common-data grids.
// Every /admin/common-data pane should feed rows through this so
// columns match the data shape — no hardcoded lists (Mike 2026-07-17).
//
// Fixed anchors (`_id`, `ticker`, `symbol`) pin left in that order
// when present. Everything else sorts alphabetically after.
//
// Filter type per column is inferred from the first non-null sampled
// value:
//   boolean → agSetColumnFilter + ✓/✗ formatter
//   number  → agNumberColumnFilter + right-align + mono
//   string  → agSetColumnFilter (default) OR agTextColumnFilter for
//             ticker/symbol so startsWith works cleanly

export interface DeriveOptions {
  /**
   * Optional per-field predicate for editable. If omitted, no columns
   * are editable. Predicate should return false for _id / __v / any
   * server-managed fields the caller wants immutable.
   */
  isEditable?: (field: string) => boolean;
  /**
   * Additional field-name overrides for the caller. Runs AFTER the
   * shared inference so component-specific tweaks (special
   * formatters, cell renderers) can layer on. Receives the shared
   * ColDef; return the mutated one (or a fresh object).
   */
  fieldOverride?: (field: string, coldef: any) => any;
}

const FIXED_ANCHORS = ['_id', 'ticker', 'symbol'];

function humanizeField(field: string): string {
  return field
    .replace(/^_/, '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fmtBool(v: any): string {
  if (v === true) return '✓';
  if (v === false) return '✗';
  return '';
}

function detectType(rows: any[], field: string, sampleN: number): 'boolean' | 'number' | 'string' {
  for (let i = 0; i < sampleN; i++) {
    const v = rows[i]?.[field];
    if (v === null || v === undefined) continue;
    const t = typeof v;
    if (t === 'boolean') return 'boolean';
    if (t === 'number') return 'number';
    return 'string';
  }
  return 'string';
}

function baseColDefFor(field: string, kind: 'boolean' | 'number' | 'string', opts: DeriveOptions): any {
  const editable = opts.isEditable ? opts.isEditable(field) : false;
  const base: any = {
    field,
    headerName: humanizeField(field),
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 120,
    maxWidth: 400,
    editable,
  };
  if (field === '_id') {
    return {
      ...base,
      pinned: 'left',
      minWidth: 180,
      maxWidth: 240,
      editable: false,
      filter: 'agTextColumnFilter',
      cellClass: 'font-mono',
      cellStyle: { 'text-align': 'left', color: '#94a3b8' },
    };
  }
  if (field === 'ticker' || field === 'symbol') {
    return {
      ...base,
      pinned: 'left',
      minWidth: 120,
      maxWidth: 160,
      filter: 'agTextColumnFilter',
      // startsWith default so single-letter symbols are findable.
      filterParams: { defaultOption: 'startsWith' } as any,
      cellClass: 'font-mono',
      cellStyle: { 'text-align': 'left', color: '#5eead4' },
      sort: 'asc' as any,
      sortIndex: 0,
    };
  }
  if (kind === 'boolean') {
    return {
      ...base,
      minWidth: 110,
      maxWidth: 140,
      filter: 'agSetColumnFilter',
      valueFormatter: (p: any) => fmtBool(p.value),
      cellStyle: { 'text-align': 'center' },
    };
  }
  if (kind === 'number') {
    return {
      ...base,
      minWidth: 110,
      maxWidth: 200,
      filter: 'agNumberColumnFilter',
      cellClass: 'font-mono',
      cellStyle: { 'text-align': 'right' },
    };
  }
  return { ...base, filter: 'agSetColumnFilter' };
}

export function deriveDynamicColumnDefs(rows: any[], opts: DeriveOptions = {}): any[] {
  if (!rows || rows.length === 0) return [];
  const keys = new Set<string>();
  for (const r of rows) {
    if (r && typeof r === 'object') {
      for (const k of Object.keys(r)) keys.add(k);
    }
  }
  const sampleN = Math.min(rows.length, 100);
  const typeOf: Record<string, 'boolean' | 'number' | 'string'> = {};
  for (const k of keys) typeOf[k] = detectType(rows, k, sampleN);

  const ordered = FIXED_ANCHORS.filter((k) => keys.has(k))
    .concat([...keys].filter((k) => !FIXED_ANCHORS.includes(k)).sort());

  return ordered.map((field) => {
    const cd = baseColDefFor(field, typeOf[field], opts);
    return opts.fieldOverride ? opts.fieldOverride(field, cd) : cd;
  });
}
